import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import TypeInput from "../common/form/TypeInput";
import { usePostTemplateUploadMutation } from "../../../../services/api/repositories/TemplateMailsRepository";
import DatePicker from 'react-datepicker';
import StatusInput from "../common/form/StatusInput";
import { NavLink, useNavigate } from "react-router-dom";
import PostTemplateMailUploadRequest from "../../../../models/api/TemplateMails/PostTemplateMailUploadRequest";
import ClientInput from "../../mailing-rules/common/form/ClientInput";
import { IClientType } from "../../../../interfaces/api/IClientsType";

export default function TemplateCreateSection() {
    const navigate = useNavigate();

    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [type, setType] = useState<string>("none");
    const descriptionRef = useRef<HTMLTextAreaElement>(null);
    const statusRef = useRef<HTMLInputElement>(null);
    const fileRef = useRef<HTMLInputElement>(null);
    const subjectRef = useRef<HTMLInputElement>(null);

    const [clientTypeList, setClientTypeList] = useState<IClientType[]>([]);
    const [clientTypeUuidList, setclientTypeUuidList] = useState<string[]>([]);

    const [show, setShow] = useState(false);
    const [errMsg, setErrMsg] = useState<string>("");

    const [showValidation, setShowValidation] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{ subject: string, description: string, type: string, client_types: string, file: string }>({ subject: "", description: "", type: "", client_types: "", file: "" });
    const [isSetByUser, setIsSetByUser] = useState<{ description: boolean, type: boolean, client_types: boolean, subject: boolean }>({ description: false, type: false, client_types: false, subject: false });

    const [postTemplateUploadMutation, { data, isSuccess, isLoading, isError, error }] = usePostTemplateUploadMutation();

    const handleClientTypeAdd = (clientType: IClientType) => {
        if (clientTypeList.some(existingClientType => existingClientType.uuid === clientType.uuid)) {
            return;
        }
        setClientTypeList([...clientTypeList, clientType]);
    };

    const handleClientTypeRemove = (value: string) => {
        setClientTypeList(clientTypeList.filter(item => item.uuid !== value));
        setclientTypeUuidList(clientTypeUuidList.filter(item => item !== value));
    }

    const handleClientTypeUuidAdd = (uuid: string) => {
        if (clientTypeUuidList.some(existingUuidClient => existingUuidClient === uuid)) {
            return;
        }
        setclientTypeUuidList([...clientTypeUuidList, uuid]);
    };

    const handleClientTypeUuidRemove = (uuid: string) => {
        setclientTypeUuidList(clientTypeUuidList.filter(item => item !== uuid));
    }

    const handleClose = useCallback(() => {
        setShow(false)
    }, [setShow]);

    const handleShow = useCallback(async () => {
        setShowValidation(true);

        let errors = validationErrors;
        let canSend = true;

        if (subjectRef.current?.value.length === 0) {
            errors.subject = "Veuilez saisir un sujet";
            setIsSetByUser(prevState => ({ ...prevState, subject: true }));
            canSend = false;
        }

        if (type === "none" && !isSetByUser.type) {
            errors.type = "Veuillez sélectionner un type";
            setIsSetByUser(prevState => ({ ...prevState, type: true }));
            canSend = false;
        }

        if (clientTypeList.length === 0) {
            errors.client_types = "Veuillez sélectionner au moins un client cible";
            setIsSetByUser(prevState => ({ ...prevState, client_types: true }));
            canSend = false;
        }

        if (descriptionRef.current?.value && descriptionRef.current?.value.length > 255) {
            errors.description = "La description ne doit pas dépasser 255 caractères";
            setIsSetByUser(prevState => ({ ...prevState, description: true }));
            canSend = false;
        }

        if (fileRef.current?.files?.[0]?.type !== "text/html") {
            errors.file = "Le fichier doit être un HTML";
            setIsSetByUser(prevState => ({ ...prevState, file: true }));
            canSend = false;
        }

        if (!canSend) {
            setValidationErrors(errors);
            return;
        }

        setShow(true)
    }, [validationErrors, type, isSetByUser.type, clientTypeList.length]);

    const handleSubmit = useCallback(async () => {
        setShowValidation(true);
        if (!descriptionRef.current?.value
            || type === "none"
            || clientTypeList.length === 0
            || !fileRef.current?.files?.[0]
            || !subjectRef.current?.value
            || !statusRef.current?.value) {
            return;
        }

        let errors = validationErrors;
        let canSend = true;

        if (descriptionRef.current?.value.length > 255) {
            errors.description = "La description ne doit pas dépasser 255 caractères";
            canSend = false;
        }

        if (fileRef.current?.files?.[0]?.type !== "text/html") {
            errors.file = "Le fichier doit être un HTML";
            canSend = false;
        }

        if (clientTypeList.length === 0) {
            errors.client_types = "Veuillez sélectionner un client";
            canSend = false;
        }

        let start_date = '';
        if (startDate !== null) {
            start_date = `${startDate?.toLocaleDateString()} 00:00:00`;
        } else {
            start_date = `${new Date().toLocaleDateString()} 00:00:00`;
        }

        let end_date = '';
        if (endDate !== null) {
            end_date = `${endDate?.toLocaleDateString()} 23:59:59`;
        }

        let status = 'inactive';
        if (statusRef.current && statusRef.current.checked) {
            status = 'active';
        }

        if (!canSend) {
            setValidationErrors(errors);
            return;
        }

        postTemplateUploadMutation(
            new PostTemplateMailUploadRequest({
                description: descriptionRef.current?.value,
                type: type,
                file: fileRef.current?.files?.[0],
                subject: subjectRef.current?.value,
                client_types: clientTypeUuidList,
                status: status,
                date_validity_start: start_date,
                date_validity_end: end_date,
            })
        );

        setShow(false);
    }, [type, clientTypeList.length, validationErrors, startDate, endDate, postTemplateUploadMutation, clientTypeUuidList]);

    const handleSubjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        if (validationErrors.subject && value.length > 0) {
            setValidationErrors({ ...validationErrors, subject: "" });
        }
    };

    const handleDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;

        if (validationErrors.description && value.length > 0) {
            setValidationErrors({ ...validationErrors, description: "" });
        }
    }

    const handleError = useCallback(() => {
        if (isError && error !== undefined && error !== null && typeof error === 'object' && (errMsg === "" || errMsg === "Une erreur a été rencontrée")) {
            if ("error" in error) {
                setErrMsg(error.error as string);
            } else if ("data" in error && typeof error.data === 'object' && error.data !== null && "message" in error.data) {
                setErrMsg(error.data.message as string);
            }
        } else if (errMsg === "") {
            setErrMsg("Une erreur a été rencontrée");
        }
    }, [isError, error, errMsg]);

    useEffect(() => {
        if (isSuccess) {
            navigate("/templates");
        }

        if (isError) {
            handleError();
        }

    }, [isSuccess, navigate, isError, handleError]);

    return (
        <>
            <div className="object template">
                {isLoading && (<h3>Enregistrement en cours</h3>)}
                {!isLoading && !isSuccess && (
                    <Form className="form" validated={showValidation}>
                        <section className="form-section id">
                            <h3>
                                <span className="icon"></span>
                                <span className="text">Définition</span>
                            </h3>
                            <Form.Group className="form-group template-name required">
                                <Form.Label className="form-label" htmlFor="template-name-input">Sujet</Form.Label>
                                <Form.Control
                                    type="text"
                                    className="form-control w100"
                                    id="template-name-input"
                                    tabIndex={1}
                                    required
                                    ref={subjectRef}
                                    onChange={handleSubjectChange}
                                    maxLength={255}
                                    isInvalid={validationErrors.subject.length > 0}
                                />
                                <Form.Control.Feedback type="invalid">{validationErrors.subject}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="form-group template-description">
                                <Form.Label className="form-label" htmlFor="template-description-input">Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    type="text"
                                    className="form-control w100"
                                    id="template-description-input"
                                    
                                    tabIndex={2}
                                    ref={descriptionRef}
                                    maxLength={255}
                                    onChange={handleDescriptionChange}
                                    isInvalid={validationErrors.description.length > 0}
                                />
                                <Form.Control.Feedback type="invalid">{validationErrors.description}</Form.Control.Feedback>
                            </Form.Group>

                            <TypeInput
                                tabIndex={3}
                                setType={setType}
                                type={type}
                                typeValidationError={validationErrors.type}
                                typeIsSetByUser={isSetByUser.type}
                                setTypeIsSetByUser={(typeIsSetByUser: boolean) => { setIsSetByUser(prevState => ({ ...prevState, type: typeIsSetByUser })) }}
                            />

                            <StatusInput tabIndex={4} ref={statusRef} />

                            <ClientInput
                                clientTypeList={clientTypeList}
                                handleClientTypeAdd={handleClientTypeAdd}
                                handleClientTypeUuidAdd={handleClientTypeUuidAdd}
                                handleClientTypeRemove={handleClientTypeRemove}
                            />

                        </section>
                        <section className="form-section schedule">
                            <h3>
                                <span className="icon" />
                                <span className="text">Validité</span>
                            </h3>
                            <div className="fromTo">
                                <Form.Group className="form-group schedule-runDateStart">
                                    <Form.Label className="form-label" htmlFor="template-runDateStart-input">Du</Form.Label>
                                    <DatePicker className="form-control w100" id="template-runDateStart-input" tabIndex={5}
                                        selected={startDate}
                                        dateFormat="dd/MM/yyyy"
                                        onChange={(date) => { setStartDate(date) }} />
                                </Form.Group>
                                <Form.Group className="form-group schedule-runDateEnd">
                                    <Form.Label className="form-label" htmlFor="template-runDateEnd-input">Au</Form.Label>
                                    <DatePicker className="form-control w100" id="template-runDateEnd-input" tabIndex={6}
                                        selected={endDate}
                                        dateFormat="dd/MM/yyyy"
                                        onChange={(date) => { setEndDate(date) }} />
                                </Form.Group>
                            </div>
                        </section>
                        <section className="form-section file">
                            <h3>
                                <span className="icon" />
                                <span className="text">Fichier</span>
                            </h3>
                            <Form.Group className="form-group template-file required">
                                <Form.Label className="form-label" htmlFor="template-file-input">Fichier</Form.Label>
                                <Form.Control
                                    type="file"
                                    className="form-control w100"
                                    id="template-file-input"
                                    tabIndex={7}
                                    ref={fileRef}
                                    required
                                    isInvalid={validationErrors.file.length > 0}
                                />
                            </Form.Group>
                        </section>

                        <div>

                            {(validationErrors.client_types) && (
                                <Alert
                                    variant="danger"
                                    dismissible>
                                    {validationErrors.client_types}
                                </Alert>
                            )}

                            {isError && data !== undefined && "message" in data && (
                                <Alert
                                    variant="danger"
                                    dismissible>
                                    {data.message}
                                </Alert>
                            )}

                            {isError && errMsg.length > 0
                                && (
                                    <>
                                        <Alert
                                            variant="danger"
                                            dismissible >
                                            {errMsg}
                                        </Alert>
                                    </>
                                )}
                        </div>

                        <div className="form-submit">
                            <NavLink to={"/templates"} type="button" className="btn btn-secondary btn-cancel" onClick={() => { }} tabIndex={13} title="Annuler">
                                <span className="icon"></span>
                                <span className="text">Annuler</span>
                            </NavLink>
                            <Button type="button" className="btn btn-primary btn-ok" onClick={() => { handleShow() }} tabIndex={12} title="Ajouter le nouveau Template email">
                                <span className="icon"></span>
                                <span className="text">Ajouter</span>
                            </Button>
                        </div>
                    </Form>
                )}
            </div>
            <SubmitModal show={show} handleClose={handleClose} handleSubmit={handleSubmit} name={fileRef.current?.files?.[0]?.name || ""} />
        </>
    );
}

function SubmitModal({ show, handleClose, handleSubmit, name }: { show: boolean, handleClose: () => void, handleSubmit: () => void, name: string }) {
    return (
        <Modal id="templateSaveChangesModalModal" show={show} onHide={() => { handleClose() }}>
            <Modal.Header closeButton>
                <h1 className="modal-title" id="templateSaveChangesModalLabel">
                    <span className="icon"></span>
                    <span className="text">Création d'un template</span>
                </h1>
            </Modal.Header>
            <Modal.Body>
                <p>Vous souhaitez créer un template</p>
                <div className="object attach" data-status="active">
                    <h2>
                        <span className="icon"></span>
                        <span className="text">{name}</span>
                    </h2>
                </div>
                <p>Veuillez valider la création de ce template</p>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="secondary"
                    className="btn-cancel"
                    data-bs-dismiss="modal"
                    title="Annuler"
                    onClick={() => { handleClose() }}
                >
                    <span className="icon"></span>
                    Annuler
                </Button>
                <Button
                    variant="primary"
                    className="btn-ok"
                    data-bs-dismiss="modal"
                    title="Valider"
                    onClick={() => { handleSubmit() }}
                >
                    <span className="icon"></span>
                    Valider
                </Button>
            </Modal.Footer>
        </Modal>
    );
}